import styled, { keyframes } from "styled-components";

/* ───────────────────────────────────────────────────────────────────────────
   Crop Modal — LIGHT WATER THEME
   Shell mirrors GenerateCollectionModal exactly (same background, ring,
   specular sheen, underglow, and gentle float breathing).
   Exports preserved so CropModal.js stays untouched.
   ─────────────────────────────────────────────────────────────────────── */

const buttonShimmer = keyframes`
  0%   { left: -100%; }
  100% { left: 150%; }
`;

const panelFloat = keyframes`
  0%, 100% { filter: brightness(1); }
  50%      { filter: brightness(1.02); }
`;

// ─── Modal shell ───────────────────────────────────────────────────────
//  Background, border, box-shadow, and pseudo-elements match
//  GenerateCollectionModal.styles.js → ModalContainer.

export const ModalContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: min(90dvw, 540px);
  max-height: 90dvh;
  padding: 0;
  border-radius: 28px;
  border: 1px solid var(--glass-edge);
  background:
    radial-gradient(
      120% 60% at 30% -10%,
      rgba(var(--glass-white-rgb), 0.96) 0%,
      rgba(var(--glass-pale-rgb), 0.9) 55%,
      rgba(var(--glass-pale-rgb), 0.82) 100%
    ),
    linear-gradient(
      180deg,
      rgba(var(--glass-white-rgb), 0.94) 0%,
      rgba(var(--glass-pale-rgb), 0.9) 100%
    );
  backdrop-filter: blur(28px) saturate(180%);
  -webkit-backdrop-filter: blur(28px) saturate(180%);
  box-shadow:
    0 32px 64px -18px rgba(var(--color-brand-500-rgb), 0.4),
    0 12px 28px rgba(var(--color-brand-400-rgb), 0.22),
    inset 0 2px 0 rgba(255, 255, 255, 0.95),
    inset 0 -10px 22px rgba(var(--color-brand-200-rgb), 0.26),
    inset 0 0 0 1px rgba(255, 255, 255, 0.45);
  animation: ${panelFloat} 5.5s ease-in-out infinite;

  /* Top specular sheen */
  &::before {
    content: "";
    position: absolute;
    top: 2px;
    left: 32px;
    right: 32px;
    height: 20px;
    border-radius: 999px;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.9) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(2px);
    pointer-events: none;
    z-index: 10;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  /* Bottom aqua underglow */
  &::after {
    content: "";
    position: absolute;
    bottom: -8px;
    left: 25%;
    right: 25%;
    height: 16px;
    background: radial-gradient(
      ellipse at center top,
      rgba(var(--color-brand-400-rgb), 0.42) 0%,
      rgba(var(--color-brand-400-rgb), 0) 70%
    );
    pointer-events: none;
    z-index: -1;
  }

  @media (max-width: 475px) {
    max-width: 92dvw;
    border-radius: 24px;
  }

  [data-theme="dark"] & {
    border-color: transparent;
  }
`;

// ─── Header ────────────────────────────────────────────────────────────

export const Header = styled.div`
  position: relative;
  z-index: 6;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 20px 14px;
  border-bottom: 1px solid rgba(var(--color-brand-300-rgb), 0.2);

  @media (max-width: 475px) {
    padding: 16px 16px 12px;
  }
`;

export const ModalTitle = styled.h2`
  color: var(--color-brand-700);
  font-family: var(--font-family-primary);
  font-size: 19px;
  font-weight: 800;
  line-height: normal;
  letter-spacing: -0.02em;
  margin: 0;
`;

/* Close — frosted water circle with top-left specular highlight */
export const CloseButton = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  border-radius: 50%;
  border: 1px solid rgba(var(--glass-white-rgb), 0.65);
  border: none;
  background: radial-gradient(
    130% 100% at 30% 15%,
    rgba(var(--glass-white-rgb), 0.82) 0%,
    rgba(var(--glass-white-rgb), 0.54) 45%,
    rgba(var(--glass-pale-rgb), 0.4) 100%
  );
  backdrop-filter: blur(18px) saturate(180%);
  -webkit-backdrop-filter: blur(18px) saturate(180%);
  box-shadow:
    0 6px 14px rgba(var(--color-brand-500-rgb), 0.22),
    inset 0 1px 0 rgba(255, 255, 255, 0.95),
    inset 0 -3px 8px rgba(var(--color-brand-200-rgb), 0.22);
  color: var(--color-brand-700);
  cursor: pointer;
  outline: none;
  overflow: hidden;
  transition:
    transform 200ms var(--ease-snappy),
    border-color 200ms var(--ease-snappy);

  &::before {
    content: "";
    position: absolute;
    top: 3px;
    left: 6px;
    width: 16px;
    height: 9px;
    border-radius: 50%;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.88) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(1px);
    pointer-events: none;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  svg {
    position: relative;
    z-index: 1;
    width: 15px;
    height: 15px;
    path {
      stroke: var(--color-brand-700);
    }
  }

  &:hover {
    transform: translateY(-1px) scale(1.04);
    border-color: rgba(var(--color-brand-400-rgb), 0.55);
  }

  [data-theme="dark"] & {
    border-color: transparent;
  }
`;

// ─── Body ──────────────────────────────────────────────────────────────

export const ModalContent = styled.div`
  position: relative;
  z-index: 6;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 18px 20px 20px;
  overflow-y: auto;

  /* Tidy the scrollbar for light theme */
  scrollbar-width: thin;
  scrollbar-color: rgba(var(--color-brand-300-rgb), 0.4) transparent;
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(var(--color-brand-300-rgb), 0.4);
    border-radius: 999px;
  }

  @media (max-width: 475px) {
    padding: 14px 14px 16px;
    gap: 16px;
  }
`;

export const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

export const Column = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 10px;
`;

export const Label = styled.span`
  color: var(--color-brand-700);
  font-family: var(--font-family-primary);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.09em;
  text-transform: uppercase;
`;

// ─── Crop area — frosted water stage ───────────────────────────────────

export const CropContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 520px;
  align-self: center;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  border-radius: 20px;
  border: 1px solid rgba(var(--color-brand-300-rgb), 0.24);
  background: radial-gradient(
    120% 80% at 30% -10%,
    rgba(var(--glass-white-rgb), 0.82) 0%,
    rgba(var(--glass-pale-rgb), 0.64) 55%,
    rgba(var(--glass-pale-rgb), 0.5) 100%
  );
  backdrop-filter: blur(14px) saturate(170%);
  -webkit-backdrop-filter: blur(14px) saturate(170%);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.9),
    inset 0 -3px 10px rgba(var(--color-brand-200-rgb), 0.18),
    0 2px 8px rgba(var(--color-brand-500-rgb), 0.08);
  overflow: hidden;

  /* Soft inner sheen along the top of the stage */
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 8%;
    right: 8%;
    height: 34%;
    z-index: 1;
    pointer-events: none;
    background: radial-gradient(
      120% 100% at 50% -10%,
      rgba(var(--glass-white-rgb), 0.55) 0%,
      rgba(var(--glass-pale-rgb), 0.18) 40%,
      rgba(232, 246, 255, 0) 82%
    );
    mix-blend-mode: screen;
  }

  .ReactCrop {
    position: relative;
    z-index: 2;
    max-height: 400px;
    border-radius: 14px;
    overflow: hidden;
  }

  /* react-image-crop overrides to match the water palette */
  .ReactCrop__crop-selection {
    border: 1.5px dashed rgba(var(--glass-white-rgb), 0.92);
    box-shadow:
      0 0 0 9999px rgba(15, 48, 92, 0.42),
      0 6px 18px rgba(var(--color-brand-900-rgb), 0.3);
  }
  .ReactCrop__drag-handle::after {
    background-color: var(--glass-base-1);
    border: 1px solid var(--color-brand-600);
    box-shadow: 0 2px 6px rgba(var(--color-brand-900-rgb), 0.35);
  }

  @media (max-width: 475px) {
    padding: 10px;
    border-radius: 16px;
  }

  [data-theme="dark"] & {
    border-color: transparent;
  }
`;

// ─── Aspect-ratio pills ────────────────────────────────────────────────

export const AspectRations = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  width: 100%;
  flex-wrap: wrap;
`;

/* Active = brand-gradient water pill (matches primary CTA pattern).
   Inactive = frosted light water pill. */
export const AspectRationButton = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 34px;
  padding: 0 16px;
  border-radius: var(--radius-pill);
  flex-shrink: 0;
  cursor: pointer;
  outline: none;
  overflow: hidden;
  font-family: var(--font-family-primary);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: -0.005em;
  transition:
    transform 200ms var(--ease-snappy),
    border-color 200ms var(--ease-snappy),
    filter 200ms var(--ease-snappy);

  color: ${(p) =>
    p.$active ? "var(--glass-text-on-pill)" : "var(--color-brand-700)"};
  text-shadow: ${(p) =>
    p.$active ? "0 1px 1px rgba(var(--color-brand-900-rgb), 0.3)" : "none"};

  border: 1px solid
    ${(p) =>
      p.$active
        ? "rgba(var(--glass-white-rgb), 0.72)"
        : "rgba(var(--color-brand-300-rgb), 0.26)"};

  background: ${(p) =>
    p.$active
      ? `radial-gradient(
          140% 100% at 20% 0%,
          rgba(255, 255, 255, 0.3) 0%,
          rgba(255, 255, 255, 0) 55%
        ),
        linear-gradient(180deg, var(--color-brand-400) 0%, var(--color-brand-600) 55%, var(--color-brand-700) 100%)`
      : `radial-gradient(
          120% 100% at 20% 0%,
          rgba(var(--glass-white-rgb), 0.82) 0%,
          rgba(var(--glass-pale-rgb), 0.58) 55%,
          rgba(var(--glass-pale-rgb), 0.46) 100%
        )`};

  backdrop-filter: ${(p) => (p.$active ? "none" : "blur(14px) saturate(180%)")};
  -webkit-backdrop-filter: ${(p) =>
    p.$active ? "none" : "blur(14px) saturate(180%)"};

  box-shadow: ${(p) =>
    p.$active
      ? `0 10px 22px rgba(var(--color-brand-600-rgb), 0.34),
         inset 0 1px 0 rgba(255, 255, 255, 0.55),
         inset 0 -3px 6px rgba(var(--color-brand-700-rgb), 0.3)`
      : `0 4px 10px rgba(var(--color-brand-500-rgb), 0.12),
         inset 0 1px 0 rgba(255, 255, 255, 0.9)`};

  /* Top sheen */
  &::before {
    content: "";
    position: absolute;
    top: 2px;
    left: 10px;
    right: 10px;
    height: 7px;
    border-radius: 999px;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, ${(p) => (p.$active ? 0.65 : 0.78)}) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(1.2px);
    pointer-events: none;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  &:hover:not([disabled]) {
    transform: translateY(-1px);
    filter: ${(p) => (p.$active ? "brightness(1.05)" : "none")};
    border-color: ${(p) =>
      p.$active
        ? "rgba(var(--glass-white-rgb), 0.85)"
        : "rgba(var(--color-brand-400-rgb), 0.55)"};
  }

  [data-theme="dark"] & {
    border-color: transparent;
  }
`;

// ─── Zoom slider ───────────────────────────────────────────────────────

export const ZoomSlider = styled.input.attrs((props) => ({
  "data-value": props.value,
  "data-min": props.min,
  "data-max": props.max,
}))`
  appearance: none;
  -webkit-appearance: none;
  width: 100%;
  height: 10px;
  border: 1px solid rgba(var(--color-brand-300-rgb), 0.28);
  border-radius: var(--radius-pill);
  background: ${(props) => {
    const percentage =
      ((props.value - props.min) / (props.max - props.min)) * 100;
    return `linear-gradient(
      90deg,
      var(--color-brand-400) 0%,
      var(--color-brand-600) ${percentage}%,
      rgba(var(--glass-pale-rgb), 0.75) ${percentage}%,
      rgba(var(--glass-pale-rgb), 0.65) 100%
    )`;
  }};
  box-shadow:
    inset 0 1px 2px rgba(var(--color-brand-900-rgb), 0.12),
    inset 0 1px 0 rgba(255, 255, 255, 0.6);
  cursor: pointer;
  outline: none;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: radial-gradient(
      circle at 35% 30%,
      #ffffff 0%,
      #e6f3ff 65%,
      var(--color-brand-200) 100%
    );
    border: 2px solid var(--color-brand-600);
    box-shadow:
      0 4px 10px rgba(var(--color-brand-600-rgb), 0.36),
      inset 0 1px 0 rgba(255, 255, 255, 0.95);
    cursor: pointer;
    transition:
      transform 180ms var(--ease-snappy),
      box-shadow 180ms var(--ease-snappy);

    &:hover {
      transform: scale(1.12);
      box-shadow:
        0 6px 14px rgba(var(--color-brand-600-rgb), 0.48),
        inset 0 1px 0 rgba(255, 255, 255, 0.95);
    }
  }

  &::-moz-range-thumb {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: radial-gradient(
      circle at 35% 30%,
      #ffffff 0%,
      #e6f3ff 65%,
      var(--color-brand-200) 100%
    );
    border: 2px solid var(--color-brand-600);
    box-shadow:
      0 4px 10px rgba(var(--color-brand-600-rgb), 0.36),
      inset 0 1px 0 rgba(255, 255, 255, 0.95);
    cursor: pointer;
  }
`;

// ─── Footer buttons ────────────────────────────────────────────────────

export const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  width: 100%;
  padding-top: 4px;

  @media (max-width: 475px) {
    flex-direction: column-reverse;
  }
`;

/* Cancel — frosted light water pill (secondary) */
export const CancelButton = styled.button`
  position: relative;
  display: flex;
  flex: 1;
  min-height: 46px;
  padding: 0 22px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: var(--radius-pill);
  border: 1px solid rgba(var(--color-brand-300-rgb), 0.26);
  background: radial-gradient(
    120% 100% at 20% 0%,
    rgba(var(--glass-white-rgb), 0.88) 0%,
    rgba(var(--glass-pale-rgb), 0.62) 55%,
    rgba(var(--glass-pale-rgb), 0.48) 100%
  );
  backdrop-filter: blur(16px) saturate(180%);
  -webkit-backdrop-filter: blur(16px) saturate(180%);
  box-shadow:
    0 6px 14px rgba(var(--color-brand-500-rgb), 0.12),
    inset 0 1px 0 rgba(255, 255, 255, 0.92);
  color: var(--color-brand-700);
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: 700;
  letter-spacing: -0.005em;
  cursor: pointer;
  outline: none;
  overflow: hidden;
  transition:
    transform 200ms var(--ease-snappy),
    border-color 200ms var(--ease-snappy),
    filter 200ms var(--ease-snappy);

  &::before {
    content: "";
    position: absolute;
    top: 2px;
    left: 14px;
    right: 14px;
    height: 8px;
    border-radius: 999px;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.78) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(1.2px);
    pointer-events: none;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    border-color: rgba(var(--color-brand-400-rgb), 0.55);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

/* Confirm — brand-gradient water pill (primary CTA) */
export const ConfirmButton = styled.button`
  position: relative;
  display: flex;
  flex: 1;
  min-height: 46px;
  padding: 0 22px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: var(--radius-pill);
  cursor: pointer;
  outline: none;
  overflow: hidden;
  color: var(--glass-text-on-pill);
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: 800;
  letter-spacing: -0.005em;
  white-space: nowrap;
  text-shadow: 0 1px 1px rgba(var(--color-brand-900-rgb), 0.3);

  border: 1px solid
    ${({ $type }) =>
      $type === "danger"
        ? "rgba(var(--glass-white-rgb), 0.65)"
        : "rgba(var(--glass-white-rgb), 0.72)"};

  background: ${({ $type }) => {
    switch ($type) {
      case "danger":
        return `radial-gradient(
          140% 100% at 20% 0%,
          rgba(255, 255, 255, 0.28) 0%,
          rgba(255, 255, 255, 0) 55%
        ),
        linear-gradient(180deg, #ff7a88 0%, #ff4757 55%, #d6263a 100%)`;
      case "success":
      case "primary":
      default:
        return `radial-gradient(
          140% 100% at 20% 0%,
          rgba(255, 255, 255, 0.3) 0%,
          rgba(255, 255, 255, 0) 55%
        ),
        linear-gradient(180deg, var(--color-brand-400) 0%, var(--color-brand-600) 55%, var(--color-brand-700) 100%)`;
    }
  }};

  box-shadow: ${({ $type }) =>
    $type === "danger"
      ? `0 12px 26px rgba(255, 71, 87, 0.36),
         inset 0 1px 0 rgba(255, 255, 255, 0.55),
         inset 0 -3px 6px rgba(180, 30, 54, 0.3)`
      : `0 14px 28px rgba(var(--color-brand-600-rgb), 0.36),
         inset 0 1px 0 rgba(255, 255, 255, 0.55),
         inset 0 -3px 6px rgba(var(--color-brand-700-rgb), 0.3)`};

  transition:
    transform 200ms var(--ease-snappy),
    filter 200ms var(--ease-snappy),
    box-shadow 200ms var(--ease-snappy),
    opacity 200ms var(--ease-snappy);

  /* Top sheen */
  &::before {
    content: "";
    position: absolute;
    top: 2px;
    left: 16px;
    right: 16px;
    height: 9px;
    border-radius: 999px;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.72) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(1.5px);
    pointer-events: none;
    z-index: 1;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  /* Moving highlight sweep */
  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 45%;
    height: 100%;
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.4) 50%,
      rgba(255, 255, 255, 0) 100%
    );
    animation: ${buttonShimmer} 4s infinite;
    pointer-events: none;
  }

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    filter: brightness(1.06);
    box-shadow: ${({ $type }) =>
      $type === "danger"
        ? `0 16px 32px rgba(255, 71, 87, 0.46),
           inset 0 1px 0 rgba(255, 255, 255, 0.7),
           inset 0 -3px 8px rgba(180, 30, 54, 0.34)`
        : `0 18px 34px rgba(var(--color-brand-600-rgb), 0.48),
           inset 0 1px 0 rgba(255, 255, 255, 0.7),
           inset 0 -3px 8px rgba(var(--color-brand-700-rgb), 0.38)`};
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    filter: brightness(0.98);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    pointer-events: none;
  }

  l-bouncy {
    position: relative;
    z-index: 2;
  }
`;
